import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

const RESERVED_WORDS = {
    IDENTITY: "class",
    PROPERTIES: "properties",
    CHILDREN: "entities",
    ACTIONS: "actions",
    VALIDATIONS: "validations"
};

const DEFAULT_ID = "DEFAULT";
const EMPTY_ENTITY_ID = "#EMPTY_ENTITY#";
const DEPTH_TRAVERSAL = "DFS";
const BREATH_TRAVERSAL = "BFS";

//simple check for Entity object
const isEntity = (entityObject) => {
    return (
        entityObject.hasOwnProperty("getId") &&
        entityObject.hasOwnProperty("isEmpty") &&
        entityObject.hasOwnProperty("toSIREN")
    );
};

const traversalBFS = (f, queue = [], position) => {
    if (position === queue.length || queue.length === 0) {
        return;
    }
    const currentEntity = queue[position];
    f(currentEntity);
    const children = currentEntity.getChildren();
    if (children.length) {
        queue.push(...children);
    }
    traversalBFS(f, queue, position + 1);
};

const traversalDFS = (f, entity) => {
    const children = entity.getChildren();
    children.forEach((child)=>{
        traversalDFS(f, child);
    })
    f(entity);
};
//input is SIREN json object
//builderOptions.inplace: Builder uses input JSON and does not make a copy
const EntityBuilder = (input = {}, builderOptions = {}) => {
    //return entity
    let entity = {};
    //initialization
    let internal;
    if (builderOptions.inplace) {
        //Builder uses input JSON and does not make a copy
        //still a new object is created so that input.entities are not changed on initialization
        internal = Object.assign({}, input);
    } else {
        internal = JSON.parse(JSON.stringify(input));
    }

    internal.class = internal.class || [DEFAULT_ID];
    internal.entities = internal.entities || [];
    internal.entities = internal.entities.map((entityData) =>
        EntityBuilder.of(entityData, builderOptions)
    );
    internal.properties = internal.properties || {};

    //id
    const getId = () => internal[RESERVED_WORDS.IDENTITY][0];
    const isEmpty = () => getId() === EMPTY_ENTITY_ID;
    const getEmptyEntity = () => EntityBuilder.of({ class: [EMPTY_ENTITY_ID] });
    //classified
    const addClassifier = (name) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        internal[RESERVED_WORDS.IDENTITY].push(name);
        return entity;
    };
    //flatten, join
    const flatten = () => {
        let result = {};
        Object.keys(internal).forEach((key) => {
            if (key === RESERVED_WORDS.CHILDREN) {
                result[RESERVED_WORDS.CHILDREN] = internal[RESERVED_WORDS.CHILDREN].map(
                    (childEntity) => childEntity.toSIREN()
                );
            } else {
                result[key] = JSON.parse(JSON.stringify(internal[key]));
            }
        });
        return result;
    };
    //property
    const setProperty = (name, value) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        internal[RESERVED_WORDS.PROPERTIES][name] = value;
        return entity;
    };
    const getProperty = (name) => internal[RESERVED_WORDS.PROPERTIES][name];
    const removeProp = (name) => {
        if (internal[RESERVED_WORDS.PROPERTIES].hasOwnProperty(name)) {
            delete internal[RESERVED_WORDS.PROPERTIES][name];
        }
        return entity;
    };
    const prop = (name, value) => setProperty(name, value);
    //label
    const setLabel = (label) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        setProperty("label", label);
        return entity;
    };
    const getLabel = () => getProperty("label");
    const label = (label) => setLabel(label);
    //value
    const setValue = (value) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        setProperty("value", value);
        return entity;
    };
    const getValue = () => getProperty("value");
    const value = (value) => setValue(value);
    const setValidations = (inputValidations = []) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        internal[RESERVED_WORDS.VALIDATIONS] = inputValidations;
        return entity;
    };
    const validations = (input) => setValidations(input);
    const addValidation = (inputValidation = {}) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        internal[RESERVED_WORDS.VALIDATIONS] = internal[RESERVED_WORDS.VALIDATIONS] || [];
        internal[RESERVED_WORDS.VALIDATIONS].push(inputValidation);
        return entity;
    };
    //validation required
    const addAction = (action) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        if (!internal[RESERVED_WORDS.ACTIONS]) {
            internal[RESERVED_WORDS.ACTIONS] = [];
        }
        internal[RESERVED_WORDS.ACTIONS].push(action);
        return entity;
    };
    const getActions = () => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return [];
        }
        if (!internal[RESERVED_WORDS.ACTIONS]) {
            internal[RESERVED_WORDS.ACTIONS] = [];
        }
        return internal[RESERVED_WORDS.ACTIONS];
    };
    const setActions = (actions) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        if (!internal[RESERVED_WORDS.ACTIONS]) {
            internal[RESERVED_WORDS.ACTIONS] = [];
        }
        internal[RESERVED_WORDS.ACTIONS] = actions;
        return entity;
    };
    //adds a child entity
    //child entity should be a valid entity, not raw SIREN data
    const addChild = (childEntity) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        if (!isEntity(childEntity)) {
            WEBUILogs.warn("Passed argument is not an Entity, use EntityBuilder to convert it.");
            return entity;
        }
        internal[RESERVED_WORDS.CHILDREN].push(childEntity);
        return entity;
    };
    //searches for a child with a given name
    //returns true/false
    const hasChild = (name) => {
        if (getId() === name) {
            return true;
        }
        if (internal[RESERVED_WORDS.CHILDREN]) {
            return internal[RESERVED_WORDS.CHILDREN].reduce(
                (found, childEntity) => (found ? found : childEntity.hasChild(name)),
                false
            );
        }
        return false;
    };
    //method for completely resetting child entities, using new list of Entity objects
    const setChildren = (childEntities = []) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        const allEntityCheck = childEntities.every((item) => isEntity(item));
        if (!allEntityCheck) {
            WEBUILogs.warn(
                "One of the passed arguments is not an Entity, use EntityBuilder to convert it."
            );
            return entity;
        }
        internal[RESERVED_WORDS.CHILDREN] = childEntities;
        return entity;
    };
    //return array of entities
    const getChildren = () => {
        return internal[RESERVED_WORDS.CHILDREN];
    };
    //finds entity child by id or entity path: id1.id2.id3
    //if child not found return EmptyEntity
    const getChild = (name = "", { deep = true } = {}) => {
        if (name.includes(".") && deep) {
            return name.split(".").reduce((foundEntity, subName, index) => {
                if (index === 0) {
                    if (getId() === subName) {
                        return entity;
                    } else {
                        return getEmptyEntity();
                    }
                }
                if (foundEntity.isEmpty()) {
                    return foundEntity;
                }
                let childWithSubName = foundEntity
                    .getChildren()
                    .find((childEntity) => childEntity.getId() === subName);
                return childWithSubName || getEmptyEntity();
            }, entity);
        } else {
            return internal[RESERVED_WORDS.CHILDREN].reduce((found, childEntity) => {
                if (!found.isEmpty()) {
                    return found;
                }
                if (childEntity.getId() === name) {
                    return childEntity;
                }
                return childEntity.getChild(name);
            }, getEmptyEntity());
        }
    };
    //applies a function on current entityBuilder object and on all children recursively
    //can be used for traversing the whole tree (using DFS or BFS traversal algorithms)
    //returns EntityBuilder object
    const traverse = (f, { algorithm = BREATH_TRAVERSAL } = {}) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        if (algorithm === BREATH_TRAVERSAL) {
            traversalBFS(f, [entity], 0);
        } else if (algorithm === DEPTH_TRAVERSAL) {
            traversalDFS(f, entity);
        } else {
            WEBUILogs.warn("Unknown traversal algorithm, use BFS or DFS.");
        }
        return entity;
    };
    //applies a function on each child, no recursive traversing
    const mapChildren = (f) => {
        if (isEmpty()) {
            WEBUILogs.warn("Operation on empty entity.");
            return entity;
        }
        internal[RESERVED_WORDS.CHILDREN] = internal[RESERVED_WORDS.CHILDREN].map(
            (childEntity, index) => f(childEntity, index)
        );
        return entity;
    };
    const toSIREN = () => flatten();
    //initializing the result entity
    //consist of multiple functions with shared state
    entity = {
        //getters
        getId, //returns string
        getChildren, //returns array of Entity objects
        getChild, //return Entity(Will be Empty Entity if not found)
        getProperty, // returns property
        getLabel, // returns properties.label
        getValue, // returns properties.value
        getActions,
        //setters, all return entity
        addClassifier,
        prop,
        label,
        value,
        validations,
        addValidation,
        addAction,
        setActions,
        setChildren,
        addChild,
        traverse,
        mapChildren,
        removeProp,
        //utilities
        hasChild, //returns true/false
        isEmpty, //returns true/false
        toSIREN // returns SIREN object
    };
    return entity;
};

EntityBuilder.of = EntityBuilder;
EntityBuilder.fromString = (jsonString) => EntityBuilder.of(JSON.parse(jsonString));
EntityBuilder.create = (name = DEFAULT_ID) => EntityBuilder.of({ class: [name] });
export default EntityBuilder;

//bind, chain, flatMap
// const flatMap = f => mapHelper(flatten(), f);
//
// const map = f => Entity.of(mapHelper(flatten(), f));
//
// const mapHelper = (result, f) => {
//     result = f(result);
//     if (result.entities) {
//         result.entities = result.entities.map(entity => mapHelper(entity, f));
//     }
//     return result;
// };
